import React, { useContext } from "react"
import { DeepReadonly } from "ts-essentials"
import { QuickAnswersContext } from "connected-components/dashboard/context/quick-answers"
import AnalyticsContext, { useAnalyticsWithProperties } from "connected-components/analytics/context"
import { useExtractQuickAnswersContextProperties } from "./extract-context-properties"

interface QuickAnswersAnalyticsProviderProps {
  children: React.ReactNode
}

/**
 * Provides Quick Answers analytics context with automatic property extraction
 * and "Quick Answers" prefix for all events.
 *
 * This provider wraps children with analytics context that includes:
 * - Quick Answers-specific context properties (question)
 * - "Quick Answers" prefix for all tracked events
 * - Inheritance from parent analytics contexts if nested (Dashboard properties)
 *
 * Should be used within a QuickAnswersContext.Provider.
 */
export function QuickAnswersAnalyticsProvider({
  children,
}: DeepReadonly<QuickAnswersAnalyticsProviderProps>) {
  const quickAnswersContext = useContext(QuickAnswersContext)

  const contextProperties = useExtractQuickAnswersContextProperties(quickAnswersContext)

  const analyticsContextValue = useAnalyticsWithProperties(contextProperties, "Quick Answers")

  return (
    <AnalyticsContext.Provider value={analyticsContextValue}>
      {children}
    </AnalyticsContext.Provider>
  )
}

export default QuickAnswersAnalyticsProvider
